import React from "react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Switch,
    TextField,
} from "@mui/material";
import {AddLinkOutlined, CloseOutlined, SearchOutlined} from "@mui/icons-material";
import type {GridColDef, GridRenderCellParams, GridRowSelectionModel} from "@mui/x-data-grid-premium";
import {DataGridPremium} from "@mui/x-data-grid-premium";
import type {ClContract} from "../contractLinkageData";
import {CL_CURRENCY} from "../contractLinkageData";
import type {ClReviewTx} from "../contractLinkageUtils";
import {
    formatCompactCurrency,
    formatDate,
    formatFullCurrency,
    formatNumber,
    withinContractPeriod,
} from "../contractLinkageUtils";
import {ChannelChip, PeriodFit} from "./LinkageCells";

type Props = {
    open: boolean;
    /** The contract the selected transactions will be linked to. */
    contract: ClContract | null;
    /** All transactions, decorated with their effective link & status. */
    rows: ClReviewTx[];
    onOpenTx: (tx: ClReviewTx) => void;
    /** Links the given transactions to the contract. */
    onAdd: (txIds: string[]) => void;
    onClose: () => void;
};

type LinkStatusFilter = 'all' | 'unlinked' | 'linked';

const EMPTY_SELECTION: GridRowSelectionModel = {type: 'include', ids: new Set()};
const ALL = 'all';

/** Group rows (when grouped by supplier) render every column with an empty row — only render leaf cells. */
const leafCell = (render: (params: GridRenderCellParams<ClReviewTx>) => React.ReactNode) =>
    (params: GridRenderCellParams<ClReviewTx>) =>
        params.rowNode.type === 'leaf' ? render(params) : null;

export const AddTransactionsDialog: React.FC<Props> = ({open, contract, rows, onOpenTx, onAdd, onClose}) => {
    const [search, setSearch] = React.useState('');
    const [entity, setEntity] = React.useState(ALL);
    const [costCenter, setCostCenter] = React.useState(ALL);
    const [category, setCategory] = React.useState(ALL);
    const [linkStatus, setLinkStatus] = React.useState<LinkStatusFilter>('all');
    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [groupBySupplier, setGroupBySupplier] = React.useState(true);
    const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>(EMPTY_SELECTION);

    // Fresh filters & selection every time the dialog opens for a contract.
    React.useEffect(() => {
        if (!open) return;
        setSearch('');
        setEntity(ALL);
        setCostCenter(ALL);
        setCategory(ALL);
        setLinkStatus('all');
        setDateFrom('');
        setDateTo('');
        setGroupBySupplier(true);
        setSelectionModel(EMPTY_SELECTION);
    }, [open, contract?.id]);

    // Everything in the company spend that is not already on this contract.
    const pool = React.useMemo(
        () => (contract ? rows.filter(tx => tx.effectiveContractId !== contract.id) : []),
        [rows, contract]);

    const {entityOptions, costCenterOptions, categoryOptions} = React.useMemo(() => {
        const optionsOf = (get: (tx: ClReviewTx) => string) => Array.from(new Set(pool.map(get))).sort();
        return {
            entityOptions: optionsOf(tx => tx.entity),
            costCenterOptions: optionsOf(tx => tx.costCenter),
            categoryOptions: optionsOf(tx => tx.category),
        };
    }, [pool]);

    const filtered = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        return pool.filter(tx =>
            (!q
                || tx.description.toLowerCase().includes(q)
                || tx.supplier.toLowerCase().includes(q)
                || tx.docNumber.toLowerCase().includes(q)
                || tx.entity.toLowerCase().includes(q)
                || tx.costCenter.toLowerCase().includes(q)
                || tx.category.toLowerCase().includes(q)
                || (tx.effectiveContractName ?? '').toLowerCase().includes(q))
            && (entity === ALL || tx.entity === entity)
            && (costCenter === ALL || tx.costCenter === costCenter)
            && (category === ALL || tx.category === category)
            && (linkStatus === 'all'
                || (linkStatus === 'unlinked' ? !tx.effectiveContractId : !!tx.effectiveContractId))
            && (!dateFrom || tx.date >= dateFrom)
            && (!dateTo || tx.date <= dateTo));
    }, [pool, search, entity, costCenter, category, linkStatus, dateFrom, dateTo]);

    const selectedTxs = React.useMemo(() => {
        if (selectionModel.type === 'include') {
            return filtered.filter(tx => selectionModel.ids.has(tx.id));
        }
        return filtered.filter(tx => !selectionModel.ids.has(tx.id));
    }, [filtered, selectionModel]);
    const selSpend = selectedTxs.reduce((s, t) => s + t.amount, 0);

    const columns = React.useMemo<GridColDef<ClReviewTx>[]>(() => contract ? [
        {
            field: 'description',
            headerName: 'Transaction',
            flex: 1.8,
            minWidth: 230,
            renderCell: leafCell(({row}) => (
                <div className="cl-tx-cell">
                    <div className="cl-tx-desc">{row.description}</div>
                    <div className="cl-tx-meta">{row.docNumber} · {row.category}</div>
                </div>
            )),
        },
        {
            field: 'supplier',
            headerName: 'Supplier',
            flex: 1.1,
            minWidth: 170,
            renderCell: leafCell(({row}) => (
                <span className="cl-supplier-cell">
                    {row.supplier}
                    {row.channel && <ChannelChip channel={row.channel}/>}
                </span>
            )),
        },
        {field: 'entity', headerName: 'Entity', width: 105},
        {field: 'costCenter', headerName: 'Cost centre', width: 180},
        {
            field: 'date',
            headerName: 'Invoice date',
            width: 115,
            renderCell: leafCell(({row}) => <span className="cl-num">{formatDate(row.date)}</span>),
        },
        {
            field: 'amount',
            headerName: 'Amount',
            type: 'number',
            width: 130,
            valueFormatter: (value: number) =>
                value === null || value === undefined ? '' : formatFullCurrency(value, CL_CURRENCY),
            cellClassName: 'cl-num cl-amount',
        },
        {
            field: 'effectiveContractName',
            headerName: 'Currently',
            flex: 1.2,
            minWidth: 180,
            renderCell: leafCell(({row}) =>
                row.effectiveContractName
                    ? <span className="cl-candidate-current">{row.effectiveContractName}</span>
                    : <span className="cl-nolink">No link</span>),
        },
        {
            field: 'fit',
            headerName: 'Period fit',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: leafCell(({row}) => <PeriodFit inPeriod={withinContractPeriod(row.date, contract)}/>),
        },
    ] : [], [contract]);

    if (!contract) return null;

    const anyFilter = search || entity !== ALL || costCenter !== ALL || category !== ALL
        || linkStatus !== 'all' || dateFrom || dateTo;

    return (
        // The app header is pinned at theme.zIndex.drawer + 1; the drawer sits at +2
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
                sx={theme => ({zIndex: theme.zIndex.drawer + 3, '& .MuiDialog-paper': {height: '90vh'}})}>
            <DialogTitle className="cl-picker-title">
                <span>
                    Add transactions to “{contract.name}”
                    <span className="cl-adddlg-subtitle">
                        Search the whole company spend — including purchases hidden behind marketplaces (e.g. Amazon),
                        payment providers (e.g. Candex) or employee reimbursements.
                    </span>
                </span>
                <IconButton size="small" onClick={onClose}><CloseOutlined/></IconButton>
            </DialogTitle>
            <DialogContent className="cl-adddlg-content">
                <div className="cl-adddlg-toolbar">
                    <TextField
                        size="small"
                        className="cl-adddlg-search"
                        placeholder="Search supplier, description, entity, cost centre…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField select size="small" label="Entity" value={entity}
                               onChange={e => setEntity(e.target.value)}>
                        <MenuItem value={ALL}>All entities</MenuItem>
                        {entityOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Cost centre" value={costCenter}
                               onChange={e => setCostCenter(e.target.value)}>
                        <MenuItem value={ALL}>All cost centres</MenuItem>
                        {costCenterOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Category" value={category}
                               onChange={e => setCategory(e.target.value)}>
                        <MenuItem value={ALL}>All categories</MenuItem>
                        {categoryOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Link status" value={linkStatus}
                               onChange={e => setLinkStatus(e.target.value as LinkStatusFilter)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="unlinked">Not linked</MenuItem>
                        <MenuItem value="linked">Linked to another contract</MenuItem>
                    </TextField>
                    <TextField size="small" type="date" label="From" value={dateFrom}
                               onChange={e => setDateFrom(e.target.value)}
                               slotProps={{inputLabel: {shrink: true}}}/>
                    <TextField size="small" type="date" label="To" value={dateTo}
                               onChange={e => setDateTo(e.target.value)}
                               slotProps={{inputLabel: {shrink: true}}}/>
                    {anyFilter && (
                        <button type="button" className="cl-quick-link" onClick={() => {
                            setSearch('');
                            setEntity(ALL);
                            setCostCenter(ALL);
                            setCategory(ALL);
                            setLinkStatus('all');
                            setDateFrom('');
                            setDateTo('');
                        }}>
                            Clear
                        </button>
                    )}
                    <span style={{flex: 1}}/>
                    <FormControlLabel
                        className="cl-groupby"
                        control={<Switch size="small" checked={groupBySupplier}
                                         onChange={(_e, on) => setGroupBySupplier(on)}/>}
                        label="Group by supplier"
                    />
                    <span className="cl-explorer-count">
                        {formatNumber(filtered.length)} transaction{filtered.length === 1 ? '' : 's'}
                    </span>
                </div>

                <div className="cl-adddlg-grid">
                    <DataGridPremium
                        className="base-table-muix auto-row-height-centered"
                        rows={filtered}
                        columns={columns}
                        getRowId={row => row.id}
                        getRowHeight={() => 'auto'}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionPropagation={{descendants: true, parents: true}}
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        onRowClick={params => {
                            // Group rows (auto-generated when grouped by supplier) are not transactions.
                            if (typeof params.id === 'string' && params.id.startsWith('tx-')) onOpenTx(params.row);
                        }}
                        rowGroupingModel={groupBySupplier ? ['supplier'] : []}
                        groupingColDef={{
                            headerName: 'Supplier',
                            minWidth: 260,
                            flex: 1.2,
                            hideDescendantCount: false,
                        }}
                        columnVisibilityModel={{supplier: !groupBySupplier}}
                        defaultGroupingExpansionDepth={0}
                        aggregationModel={{amount: 'sum'}}
                        pagination
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: {paginationModel: {pageSize: 50, page: 0}},
                            sorting: {sortModel: [{field: 'amount', sort: 'desc'}]},
                        }}
                        sx={{
                            border: 'none',
                            '--rowBorderColor': 'rgba(0,0,0,0.08)',
                            '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700},
                            '& .MuiDataGrid-row': {cursor: 'pointer'},
                        }}
                    />
                </div>

                <div className="cl-adddlg-footer">
                    <span className="cl-adddlg-footer-summary">
                        {selectedTxs.length > 0
                            ? <><b>{formatNumber(selectedTxs.length)}</b> selected
                                · {formatCompactCurrency(selSpend, CL_CURRENCY)}</>
                            : 'Select the transactions that belong to this contract.'}
                    </span>
                    <span className="cl-adddlg-footer-actions">
                        <Button size="small" onClick={onClose}>Cancel</Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddLinkOutlined/>}
                            disabled={selectedTxs.length === 0}
                            onClick={() => onAdd(selectedTxs.map(t => t.id))}>
                            Add {selectedTxs.length > 0 ? formatNumber(selectedTxs.length) : ''} to this contract
                        </Button>
                    </span>
                </div>
            </DialogContent>
        </Dialog>
    );
};
